<template>
  <div class="step2-container">
    <!-- 환자 정보 입력 -->
    <CustomerInfoForm
      ref="customerInfoForm"
      v-model="customerInfo"
      :windowInnerWidthGreaterThan1000="true"
      :showDoctorInput="false"
      :isCustomDoctor="false"
      :doctorList="[]"
      :sendChannel="sendChannel"
      :language="language"
      :countryCodeList="countryCodeList"
      :supportMsgChannel="supportMsgChannel"
      :channelConfig="channelConfig"
      @update:customerInfo="handleCustomerInfoUpdate"
      @update:sendChannel="handleSendChannelUpdate"
      @update:language="handleLanguageUpdate"
      @update:canLeavePage="handleCanLeavePageUpdate"
      @focus-on-next-step-button="handleFocusOnNextStepButton"
      @update:isCustomDoctor="handleIsCustomDoctorUpdate"
    />

    <!-- 작업자 정보 입력 -->
    <WorkerInfoForm
      style="margin-top: 15px;"
      ref="workerInfoForm"
      v-model="workerInfo"
      :windowInnerWidthGreaterThan1000="true"
    />
  </div>
</template>

<script>
import { getProfile } from '@/utils/tokenStore';
import CustomerInfoForm from './CustomerInfoForm.vue';
import WorkerInfoForm from './WorkerInfoForm.vue';
import { commonEnum } from './enums/common';
import { localStorageEnums } from "@/enums/localStorage.enums";

export default {
  name: 'Step2',
  components: {
    CustomerInfoForm,
    WorkerInfoForm
  },
  data() {
    return {
      customerInfo: {
        email: '',
        mobile: '',
        name: '',
        gender: '',
        countryCode: '+82',
        birth: null,
        surgeryDate: (() => {
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          return `${year}/${month}/${day}`;
        })(),
        agreements: [],
        doctor: '',
        id: '',
        teethNos: [],
        ongoingSurgeries: [],
        chartNumber: '' // 차트번호 추가
      },
      workerInfo: {
        name: '',
        workDate: (() => {
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          return `${year}/${month}/${day}`;
        })(),
        prostheticDeliveryDate: (() => {
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          return `${year}/${month}/${day}`;
        })() // 보철 납품일 기본값을 오늘 날짜로 설정
      },
      sendChannel: commonEnum.sendChannel.KAKAOTALK,
      language: commonEnum.language.KO,
      countryCodeList: [],
      // 위챗 채널 제거(2026-09-10): 위챗 발행은 2026-08-29 부터 china-inapi 로 이관돼 inapi 가 400 으로
      // 거부하고, 위챗 고객 조회(by/mobile/v2/WECHAT)도 inapi 에서 제거된다. 제조사 작업지시에는
      // china 발행 경로가 없으므로 선택지 자체를 내린다.
      supportMsgChannel: ['KAKAOTALK', 'EMAIL'],
      channelConfig: {
        KAKAOTALK: {
          icon: 'fa-solid fa-comment',
          style: { color: '#3C1E1E' },
          translationKey: 'dent_issue_certificate_card_container_language_sendtype_kakao'
        },
        EMAIL: {
          icon: 'fa-solid fa-envelope',
          style: { color: '#0066CC' },
          translationKey: 'dent_issue_certificate_card_container_language_sendtype_email'
        }
      }
    };
  },
  created() {
    this.countryCodeList = [
      { value: '+82', label: this.$t('workorder_country_kr') },
      { value: '+86', label: this.$t('workorder_country_cn') },
      { value: '+1', label: this.$t('workorder_country_us') }
    ];

    // locale에 따른 기본값 설정
    this.initDefaultsByLocale();
  },
  methods: {
    // locale에 따른 기본값 초기화
    initDefaultsByLocale() {
      try {
        const companyInfo = JSON.parse(getProfile(localStorageEnums.CompanyInfo));
        const locale = companyInfo?.locale || 'ko';

        // locale별 기본값 매핑
        const localeDefaults = {
          ko: {
            countryCode: '+82',
            sendChannel: commonEnum.sendChannel.KAKAOTALK,
            language: commonEnum.language.KO
          },
          zh: {
            countryCode: '+86',
            // 위챗 채널 제거에 따라 카카오톡으로 — 선택지 밖 값이 상태에 남지 않도록
            sendChannel: commonEnum.sendChannel.KAKAOTALK,
            language: commonEnum.language.ZH
          },
          en: {
            countryCode: '+1',
            sendChannel: commonEnum.sendChannel.EMAIL,
            language: commonEnum.language.EN
          }
        };

        // 해당 locale의 기본값 적용 (없으면 ko 기본값 사용)
        const defaults = localeDefaults[locale] || localeDefaults.ko;

        this.customerInfo.countryCode = defaults.countryCode;
        this.sendChannel = defaults.sendChannel;
        this.language = defaults.language;
      } catch (error) {
        console.error('locale 기본값 설정 오류:', error);
      }
    },

    getTodayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}/${month}/${day}`;
    },
    // CustomerInfoForm의 이벤트 핸들러들
    handleCustomerInfoUpdate(customerInfo) {
      this.customerInfo = customerInfo;
    },

    handleSendChannelUpdate(sendChannel) {
      this.sendChannel = sendChannel;
    },

    handleLanguageUpdate(language) {
      this.language = language;
    },

    handleCanLeavePageUpdate(canLeavePage) {
      // 필요시 처리
    },

    handleFocusOnNextStepButton() {
      // 필요시 처리
    },

    handleIsCustomDoctorUpdate(isCustomDoctor) {
      // 필요시 처리
    },

    // 부모 컴포넌트에서 호출할 검증 메서드 (기존 Step2 호환성 유지)
    async validateForm() {
      let customerValid = false;
      let workerValid = false;

      if (this.$refs.customerInfoForm) {
        customerValid = await this.$refs.customerInfoForm.validateForm();
      }

      if (this.$refs.workerInfoForm) {
        workerValid = await this.$refs.workerInfoForm.validateForm();
      }

      return customerValid && workerValid;
    },

    // 부모 컴포넌트에서 호출할 데이터 가져오기 메서드 (기존 Step2 호환성 유지)
    getFormData() {
      let customerData = {};
      let workerData = {};

      if (this.$refs.customerInfoForm) {
        const data = this.$refs.customerInfoForm.getFormData();
        // API 요구사항에 맞게 필드명 매핑
        customerData = {
          name: data.name,
          mobile: data.mobile?.replace(/\D/g, '') || '', // 숫자만 추출
          displayMobile: data.mobile || '',
          email: data.email || '',
          countryCode: data.countryCode || '+82',
          gender: data.gender || '',
          birthDate: data.birth || '',
          surgeryDate: data.surgeryDate || '',
          chartNumber: data.chartNumber || '' // 차트번호 추가
        };
      } else {
        customerData = {
          name: this.customerInfo.name,
          mobile: this.customerInfo.mobile?.replace(/\D/g, '') || '',
          displayMobile: this.customerInfo.mobile || '',
          email: this.customerInfo.email || '',
          countryCode: this.customerInfo.countryCode || '+82',
          gender: this.customerInfo.gender || '',
          birthDate: this.customerInfo.birth || '',
          surgeryDate: this.customerInfo.surgeryDate || '',
          chartNumber: this.customerInfo.chartNumber || '' // 차트번호 추가
        };
      }

      if (this.$refs.workerInfoForm) {
        workerData = this.$refs.workerInfoForm.getFormData();
      } else {
        workerData = {
          name: this.workerInfo.name || '',
          workDate: this.workerInfo.workDate || '',
          prostheticDeliveryDate: this.workerInfo.prostheticDeliveryDate || '' // 보철 납품일 추가
        };
      }

      return {
        ...customerData,
        worker: workerData,
        // 언어 및 전송방식 정보 추가
        language: this.language,
        sendChannel: this.sendChannel
      };
    }
  }
};
</script>
